/**
 * The connected Xero tenant's **invoice branding themes**, for a picker.
 *
 * A branding theme is the template a document is printed against — the logo,
 * the payment terms, the bank details on the footer. `SEED GBP` and
 * `SEED HSBC` on this tenant differ only in which account a customer is told
 * to pay into, which is why the wrong one is not a cosmetic mistake.
 *
 * ## Why this mirrors `xero-tax-rates.ts` rather than inventing a shape
 *
 * Both are "read a list from the live Xero tenant for a dropdown", and that
 * job has four problems worth solving once: not asking while there is no
 * tenant to ask, telling a dead connection apart from a slow one, letting a
 * failed read be retried in place, and never letting a failure look like an
 * answer. `useXeroTaxRates` already settled all four, so the state shape, the
 * `enabled` flag and the failure kinds here are deliberately identical.
 *
 * The **messages** are not shared: `describeTaxRateFailure` names tax rates in
 * every sentence, and a branding-theme read reporting "the Xero tax rates
 * could not be read" sends the reader to the wrong control. Unifying the two
 * means giving that function a subject parameter, which is an edit to a file
 * this change does not own — see {@link describeBrandingThemeFailure}.
 *
 * ## The rate-limit cooldown is NOT here either
 *
 * A fifth problem turned up after the four above: `429 XERO_RATE_LIMITED`
 * carries a `retryAfterSeconds` that has been as long as **13.7 hours**, and
 * nothing was reading it — so all five consumers of this hook re-asked on every
 * form open and each got a red box. That state belongs to the tenant's shared
 * call budget rather than to this list, so it lives in `xero-cooldown.ts` at
 * module scope and is consulted by `xero-tax-rates.ts` too. One form learning
 * about the cooldown stops the other eight asking.
 *
 * ## What is NOT here
 *
 * No JSX. The caller lays the control out; this file is the data and the
 * vocabulary, exactly as `xero-tax-rates.ts` is for its two fields.
 */

import { useCallback, useEffect, useState } from 'react';
import type { ComboboxOption } from '@/components/ui';
import { ApiError, api } from './api';
import type { XeroBrandingTheme } from './types';
import {
  XeroCooldownError,
  clearXeroCooldown,
  noteXeroRateLimit,
  xeroCooldownMessage,
  xeroCooldownSecondsRemaining,
} from './xero-cooldown';

/** The noun every message in this file uses for the list it reads. */
const SUBJECT = 'branding themes';

/**
 * Why the themes could not be read, in terms of **what the reader should do**.
 *
 * `XERO_TOKEN_REJECTED` and `XERO_UPSTREAM_UNAVAILABLE` are both `502` and they
 * need opposite advice — one says re-authorise the connection, the other says
 * nothing is wrong, wait. Branching on the status would give whichever of those
 * two sentences was written first to both, and the wrong one sends someone
 * either to reconnect a healthy connection or to sit waiting on a dead one.
 *
 * ⚠️ **`cooldown` is not a flavour of `retry`, and must never be folded into
 * it.** `retry` means "Xero did not answer, try again shortly" — advice that is
 * actively wrong under a rate limit, where trying again spends another request
 * out of a budget that is already empty and cannot succeed until it resets.
 * The two also want different tones: a cooldown is a calm, expected state of a
 * healthy connection, not a fault. See `xero-cooldown.ts`.
 */
export interface BrandingThemesFailure {
  kind: 'reconnect' | 'retry' | 'missing' | 'cooldown' | 'other';
  message: string;
  /**
   * Seconds until Xero's limit resets. Set only on `kind: 'cooldown'`, where it
   * lets a caller phrase the wait its own way — a folded panel has room for a
   * clause where a field has room for the whole sentence.
   */
  retryAfterSeconds?: number;
}

export interface BrandingThemesState {
  /** In the tenant's own `sortOrder` — see {@link sortBrandingThemes}. */
  themes: XeroBrandingTheme[];
  loading: boolean;
  /**
   * Non-null when the list could not be read.
   *
   * `api.xeroBrandingThemes()` throws rather than resolving to `[]`, so this
   * can never be confused with a tenant that genuinely has no themes. It must
   * always reach the screen: a silently empty dropdown reads as "this
   * organisation has no branding themes", which is indistinguishable from a
   * real answer and would let someone save a blank believing they had chosen.
   */
  error: BrandingThemesFailure | null;
  reload: () => void;
}

/**
 * The same four-way model as `describeTaxRateFailure`, worded for themes.
 *
 * ⚠️ **This is a deliberate duplicate, not an oversight.** The two differ only
 * in the noun, and the clean fix is one describer taking the subject as an
 * argument — which means editing `xero-tax-rates.ts`. If both are still here
 * next time someone touches either, unify them; do not instead import the tax
 * messages, which would name the wrong control on every failure.
 */
export function describeBrandingThemeFailure(caught: unknown): BrandingThemesFailure {
  /*
   * ⚠️ FIRST, before the `instanceof ApiError` gate — a suppressed read is
   * rejected with a `XeroCooldownError`, which is not an `ApiError` and would
   * otherwise fall out of the bottom as "check your connection and try again":
   * the one piece of advice guaranteed to be wrong here, since the connection
   * is healthy and trying again is what emptied the budget.
   *
   * `noteXeroRateLimit` also RECORDS the cooldown. This describer is the single
   * funnel every branding-theme failure passes through, so recording here is
   * what makes the suppression apply to all five consumers without any of them
   * knowing it exists.
   */
  const cooldown = noteXeroRateLimit(caught);
  if (cooldown !== null) {
    return {
      kind: 'cooldown',
      message: xeroCooldownMessage(SUBJECT, cooldown),
      retryAfterSeconds: cooldown,
    };
  }
  if (!(caught instanceof ApiError)) {
    return {
      kind: 'other',
      message:
        'The Xero branding themes could not be reached. Check your connection and try again.',
    };
  }
  if (caught.code === 'XERO_TOKEN_REJECTED') {
    return {
      kind: 'reconnect',
      message:
        'Xero refused our credentials, so its branding themes could not be read. The connection needs re-authorising before a theme can be chosen here.',
    };
  }
  if (caught.code === 'XERO_UPSTREAM_UNAVAILABLE') {
    return {
      kind: 'retry',
      message:
        'Xero did not answer, so its branding themes could not be read. The connection itself is fine — try again shortly.',
    };
  }
  if (caught.status === 404) {
    return {
      kind: 'missing',
      message: 'Branding themes are not set up on the API yet, so a theme cannot be chosen here.',
    };
  }
  if (caught.status === 401 || caught.status === 403) {
    return {
      kind: 'other',
      message:
        'Your account is not permitted to read the Xero branding themes, so a theme cannot be chosen here.',
    };
  }
  return {
    kind: 'other',
    message: caught.message || 'The Xero branding themes could not be read.',
  };
}

/**
 * The tenant's own order, which is the order Xero's own screens present.
 *
 * **Never sort by name.** `Standard, SEED GBP, SEED HSBC, Lead Bank, Test` is
 * the order the person choosing has in front of them in Xero, and the default
 * theme is conventionally first rather than alphabetically first — an
 * alphabetical list quietly moves it and makes "the top one" the wrong answer.
 *
 * Sorted here as well as server-side because the guarantee is cheap and the
 * failure is invisible: a list that arrives in insertion order looks fine and
 * is simply wrong.
 *
 * ⚠️ **`sortOrder` is nullable, and `null` sorts LAST rather than as `0`.**
 * The backend falls back to `null` when Xero omits the field, and coercing that
 * to zero would put an unordered theme *first* — ahead of the tenant's default,
 * which conventionally holds `sortOrder: 0` and is the one theme whose position
 * anybody relies on. "Unknown" and "first" are opposite claims; only one of
 * them is honest about a value Xero declined to give. Ties fall back to the
 * name so the order is total, and therefore stable between renders.
 */
export function sortBrandingThemes(themes: readonly XeroBrandingTheme[]): XeroBrandingTheme[] {
  return [...themes].sort((a, b) => {
    if (a.sortOrder === null || b.sortOrder === null) {
      if (a.sortOrder === b.sortOrder) return a.name.localeCompare(b.name);
      return a.sortOrder === null ? 1 : -1;
    }
    return a.sortOrder - b.sortOrder || a.name.localeCompare(b.name);
  });
}

/**
 * The connected tenant's branding themes, read once per opening.
 *
 * `enabled` is false while the connection is still being read and whenever
 * there is no connection: there is no tenant to ask, and a request that can
 * only fail would put a red block on a form whose real state is "Xero is not
 * connected" — a different sentence with a different action.
 */
export function useXeroBrandingThemes(enabled: boolean): BrandingThemesState {
  const [themes, setThemes] = useState<XeroBrandingTheme[]>([]);
  const [error, setError] = useState<BrandingThemesFailure | null>(null);
  const [nonce, setNonce] = useState(0);
  /*
   * Which attempt has finished, rather than a `loading` flag set from inside
   * the effect. `enabled` flips from false to true when the connection read
   * settles, and a flag would have to be raised synchronously in the effect to
   * cover that — deriving it during render covers it without the extra pass.
   */
  const [settled, setSettled] = useState<number | null>(null);

  // A failed read must be retryable in place. The alternative is telling
  // someone the themes could not be read and offering them nothing but a
  // reload of the whole form, which throws away everything they had typed.
  //
  // ⚠️ Clearing the cooldown is what makes this button honest. Without it a
  // retry during a rate limit would be swallowed by the suppression below and
  // re-render the identical message, which is worse than offering nothing: the
  // reader is told there is something they can do and then nothing happens.
  const reload = useCallback(() => {
    clearXeroCooldown();
    setError(null);
    setNonce((value) => value + 1);
  }, []);

  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    /*
     * A known cooldown short-circuits the request — and does it by REJECTING
     * rather than by returning early, so the state below is written by exactly
     * the same `.catch`/`.finally` pair a real refusal would take.
     *
     * Two things fall out of that and both are requirements. The themes stay
     * `[]` with a non-null `error`, which is the branch every consumer already
     * uses to keep a stored letterhead visible and still clearable; and
     * `settled` is still advanced, so the control does not sit on a loading
     * skeleton for a request that was never made.
     */
    const waiting = xeroCooldownSecondsRemaining();
    const read: Promise<XeroBrandingTheme[]> =
      waiting === null
        ? api.xeroBrandingThemes(controller.signal)
        : Promise.reject(new XeroCooldownError(waiting));
    read
      .then((data) => {
        if (controller.signal.aborted) return;
        setThemes(sortBrandingThemes(data));
        setError(null);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setThemes([]);
        setError(describeBrandingThemeFailure(caught));
      })
      .finally(() => {
        if (!controller.signal.aborted) setSettled(nonce);
      });
    return () => controller.abort();
  }, [enabled, nonce]);

  return {
    themes: enabled ? themes : [],
    loading: enabled && settled !== nonce,
    error: enabled ? error : null,
    reload,
  };
}

/** Marks an option the live tenant list no longer carries. */
export const THEME_NOT_IN_LIST = 'Not in the current Xero list';

/**
 * The themes as picker options, with the **stored** theme injected when the
 * live list no longer carries it.
 *
 * An accountant can delete or rename a branding theme long after a document was
 * filed against it. Dropped from the options, the control renders blank — which
 * reads as "use the default" and is one stray save away from silently changing
 * which template a customer's document is printed with. So the stored id stays
 * selectable, labelled with whatever name we cached for it.
 *
 * Two details that are correctness rather than tidiness:
 *
 * - The injected row goes **first**, not into `sortOrder` position: it has no
 *   place in the tenant's order because the tenant no longer lists it, and
 *   burying it mid-list makes it look current.
 * - It is only *described* as missing when there is a list to be missing from.
 *   With `themes` empty — the read failed, or Xero is not connected — absence
 *   proves nothing, and asserting it would be a claim built on no data.
 *
 * Ids are compared case-insensitively: a GUID's case is not significant and
 * neither Xero nor our own store guarantees which form comes back.
 *
 * No "none" row is added here. That is the `Combobox`'s own
 * `emptyOptionLabel`, and its wording belongs to the screen — "use the
 * organisation default" is a different sentence on a company from the one on a
 * document being pushed.
 */
export function brandingThemeOptions(
  themes: readonly XeroBrandingTheme[],
  storedId: string | null,
  storedName: string | null,
): ComboboxOption[] {
  const list: ComboboxOption[] = sortBrandingThemes(themes).map((theme) => ({
    value: theme.brandingThemeId,
    label: theme.name,
  }));

  if (
    storedId &&
    !themes.some((theme) => theme.brandingThemeId.toLowerCase() === storedId.toLowerCase())
  ) {
    list.unshift({
      value: storedId,
      // The id is the only label left when no name was cached. It reads badly
      // and is still better than a blank row, which reads as a choice.
      label: storedName ?? storedId,
      ...(themes.length > 0 ? { hint: THEME_NOT_IN_LIST } : {}),
    });
  }

  return list;
}
